// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the one way a picture preview is drawn: a tile, a thumbnail, a picture in a reply or a bubble.
// It's a plain <img> that takes the caller's class and size, shows only once it can draw (so it
// fades in rather than painting down the screen) and says when it can't. Pair it with usePictureSource
// when the bytes have to be read first. The full-screen picture is the media viewer's.

import {clsx} from 'clsx';
import type React from 'react';
import {forwardRef, useCallback, useEffect, useRef, useState} from 'react';
import styles from './Picture.module.css';

type PictureProps = React.ImgHTMLAttributes<HTMLImageElement> & {alt: string};

export const Picture = forwardRef<HTMLImageElement, PictureProps>(function Picture(
	{alt, className, src, onLoad, onError, ...rest},
	forwarded,
) {
	const element = useRef<HTMLImageElement | null>(null);
	const [ready, setReady] = useState<'fade' | 'at-once' | null>(null);
	const attach = useCallback(
		(image: HTMLImageElement | null) => {
			element.current = image;
			if (typeof forwarded === 'function') forwarded(image);
			else if (forwarded) forwarded.current = image;
		},
		[forwarded],
	);
	// A picture the browser already has is complete before it's on screen, and never says it loaded.
	useEffect(() => {
		setReady(element.current?.complete && element.current.naturalWidth > 0 ? 'at-once' : null);
	}, [src]);
	return (
		<img
			{...rest}
			ref={attach}
			src={src}
			alt={alt}
			className={clsx(styles.picture, className)}
			data-ready={ready ?? undefined}
			onLoad={(event) => {
				setReady((current) => current ?? 'fade');
				onLoad?.(event);
			}}
			onError={(event) => {
				// Whatever the alt text or the broken picture looks like, it isn't left invisible.
				setReady((current) => current ?? 'at-once');
				onError?.(event);
			}}
		/>
	);
});
